import React, { useState, useEffect } from 'react';

const WorkOrderForm = ({ eventData, myResources, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    billToCustomer: 0,
    starts: '',
    ends: '',
    assignments: {},
  });

  // Effect to populate form when eventData changes
  useEffect(() => {
    if (eventData) {
      const startHour = new Date(eventData.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const endHour = new Date(eventData.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const initialAssignments = {};
      myResources.forEach(group => {
        if (group.children) {
          group.children.forEach(category => {
            if (category.children) {
              const categoryName = category.name.toUpperCase().replace(/ /g, '_');
              initialAssignments[categoryName] = category.children.map(resource => ({
                id: resource.id,
                name: resource.name,
                checked: eventData.resource.includes(resource.id),
              }));
            }
          });
        }
      });

      setFormData({
        title: eventData.title,
        location: eventData.location,
        billToCustomer: eventData.cost,
        starts: startHour,
        ends: endHour,
        assignments: initialAssignments,
      });
    }
  }, [eventData, myResources]);

  const handleCheckChange = (category, id) => {
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        [category]: prev.assignments[category].map(item =>
          item.id === id ? { ...item, checked: !item.checked } : item
        )
      }
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedResourceIds = Object.values(formData.assignments)
      .flatMap(categoryItems => categoryItems)
      .filter(item => item.checked)
      .map(item => item.id);

    const updatedEvent = {
      ...eventData, // Keep original event data like start/end datetimes
      title: formData.title,
      location: formData.location,
      cost: formData.billToCustomer,
      resource: updatedResourceIds,
    };
    onSave(updatedEvent);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this work order?')) {
      onDelete(eventData.title); // Assuming title is unique enough for deletion
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md md:max-h-[70vh] md:overflow-y-auto">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Edit work order</h1>

      <form onSubmit={handleSubmit}>
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bill to customer ($)
            </label>
            <input
              type="number"
              name="billToCustomer"
              value={formData.billToCustomer}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        {/* Notes Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3 text-gray-800">NOTES</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Starts</label>
              <input
                type="text"
                name="starts"
                value={formData.starts}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                readOnly // Make it readOnly as time is derived from eventData
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
              <input
                type="text"
                name="ends"
                value={formData.ends}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                readOnly // Make it readOnly
              />
            </div>
          </div>
        </div>

        {/* Assignments Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3 text-gray-800">ASSIGNMENTS</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(formData.assignments).map(([category, items]) => (
              <div key={category} className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-3">{category.replace(/_/g, ' ')}</h3>
                <div className="space-y-2">
                  {items.map(item => (
                    <div key={item.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => handleCheckChange(category, item.id)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <label className="ml-2 text-gray-700">{item.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-t pt-6">
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 mb-3 sm:mb-0"
          >
            Delete work order
          </button>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WorkOrderForm;